import { useMemo } from 'react';

import pluralize from 'pluralize';

import { CAST_AI } from '@cast/constants';
import {
  Button,
  mergeSx,
  SxProps,
  Typography,
  Box,
  tokens,
} from '@cast/design-system';

import { useAbility } from '@cast/shared-ability-data-access';
import { useActiveClusters } from 'features/organization/_hooks';
import { useEnableCastFeaturesDialog } from 'hooks/dialogs/cluster/useEnableCastFeaturesDialog';
import { useSecurityInsightsAgents } from 'hooks/queries/security-insights';
import { ContentContainer } from 'main-layout/ContentContainer';

import { useClustersOrFixture } from '../hooks/useClustersOrFixture';

type Props = { sx?: SxProps };

export const EnableSecurityFeatureHeader = ({ sx }: Props) => {
  const { openEnableCastFeaturesDialog } = useEnableCastFeaturesDialog();
  const { clusters: _clusters } = useActiveClusters();
  const { clusters } = useClustersOrFixture(_clusters);
  const clusterIds = useMemo(
    () => clusters?.map((cluster) => cluster.id) || [],
    [clusters]
  );
  const { agentsData, isLoading: isLoadingAgents } = useSecurityInsightsAgents({
    clusterIds,
    enabled: !!clusterIds.length,
  });

  const [canEdit, canCreate] = useAbility().canMany([
    ['update', 'Clusters'],
    ['create', 'Clusters'],
  ]);

  const totalClusterCount = clusters.length;
  const clusterWithoutAgentCount = useMemo(
    () =>
      clusters?.filter((cluster) => !agentsData?.agentStatuses?.[cluster.id])
        ?.length ?? 0,
    [agentsData, clusters]
  );

  if (
    isLoadingAgents ||
    clusterWithoutAgentCount === 0 ||
    totalClusterCount === clusterWithoutAgentCount ||
    !(canCreate && canEdit)
  ) {
    return null;
  }

  return (
    <Box
      sx={mergeSx(
        {
          backgroundColor: '#fff',
          borderTop: tokens.theme.border.structural.neutral,
          borderBottom: tokens.theme.border.structural.neutral,
        },
        sx
      )}
      data-testid="enable-security-feature-header"
    >
      <ContentContainer
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 49,
        }}
      >
        <Typography display="flex" alignContent="center" gap={8} variant="A1">
          {clusterWithoutAgentCount} out of{' '}
          {pluralize('cluster', totalClusterCount, true)}{' '}
          {clusterWithoutAgentCount > 1 ? 'do' : 'does'} not have the {CAST_AI}{' '}
          security feature enabled.
          <Typography
            variant="P12R"
            color="textSecondary"
            fontStyle="italic"
            lineHeight="inherit"
          >
            Missing clusters are not reflected in the majority of security
            reports.
          </Typography>
        </Typography>
        <Button
          disabled={!canEdit}
          size="sm"
          onClick={() => {
            openEnableCastFeaturesDialog();
          }}
        >
          Enable security
        </Button>
      </ContentContainer>
    </Box>
  );
};
